import { config } from './config';
import { getPodcastShowById } from '../data/podcastShows';

export type YouTubeEpisode = {
  id: string;
  title: string;
  guestName: string;
  guestTitle: string;
  thumbnailUrl: string;
  duration: string;
  publishedAt: string;
  watchUrl: string;
};

export const YOUTUBE_CHANNEL_VIDEOS_URL =
  config.youtubeChannelVideosUrl || 'https://www.youtube.com/@SBR2THTalent/videos';

/** In-page YouTube embed (keeps playback on the Resources page). */
/** Extract a YouTube video ID from watch, embed, or youtu.be URLs. */
export function parseYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match?.[1] ?? null;
}

export function youtubeEmbedUrl(videoId: string, autoplay = false): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  if (autoplay) params.set('autoplay', '1');
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

export type YouTubeThumbnailSize = 'maxres' | 'sd' | 'hq' | 'mq';

const YOUTUBE_THUMB_FILE: Record<YouTubeThumbnailSize, string> = {
  maxres: 'maxresdefault',
  sd: 'sddefault',
  hq: 'hqdefault',
  mq: 'mqdefault',
};

/** Best-effort YouTube still — maxres (1280×720) with smaller fallbacks when unavailable. */
export function youtubeThumbnailUrl(videoId: string, size: YouTubeThumbnailSize = 'maxres'): string {
  return `https://i.ytimg.com/vi/${videoId}/${YOUTUBE_THUMB_FILE[size]}.jpg`;
}

/** Prefer API maxres/standard, then construct high-res CDN URLs from the video id. */
export function resolveYouTubeThumbnailUrl(
  videoId: string,
  apiThumbnails?: {
    maxres?: { url: string };
    standard?: { url: string };
    high?: { url: string };
    medium?: { url: string };
  },
): string {
  return (
    apiThumbnails?.maxres?.url ??
    apiThumbnails?.standard?.url ??
    youtubeThumbnailUrl(videoId, 'maxres')
  );
}

const DEFAULT_CHANNEL_ID = 'UC4GNNAe-7dAU1qvSV6UHHAA';

/** YouTube Shorts are ≤60s; reject anything under 2 minutes. */
const MIN_VIDEO_SECONDS = 120;
/** Non–C-Suite Edge uploads must be at least 10 minutes. */
const MIN_LONGFORM_SECONDS = 600;

const PODCAST_TITLE_PATTERNS = [
  /c[\s-]?suite\s*edge/i,
  /inside\s+human\s+capital/i,
  /inside\s+open\s+technologies/i,
  /drowning\s+in\s+the\s+tech\s+talent/i,
  /drowning\s+in\s+the\s+talent/i,
  /sbr2th\s+.*podcast/i,
  /\|\s*the\s+c[\s-]?suite\s*edge/i,
];

export const PODCAST_SHOW_TITLE_PATTERNS: Record<string, RegExp[]> = {
  'c-suite-edge': [/c[\s-]?suite\s*edge/i, /\|\s*the\s+c[\s-]?suite\s*edge/i],
  'inside-human-capital': [/inside\s+human\s+capital/i],
  'inside-open-technologies': [/inside\s+open\s+technologies/i],
  'drowning-in-the-talent-pool': [
    /drowning\s+in\s+the\s+tech\s+talent/i,
    /drowning\s+in\s+the\s+talent/i,
  ],
};

export function episodeMatchesPodcastShow(title: string, showId: string): boolean {
  const patterns = PODCAST_SHOW_TITLE_PATTERNS[showId];
  if (!patterns?.length) return false;
  return patterns.some((pattern) => pattern.test(title));
}

function parseGuestFromTitle(title: string): { guestName: string; guestTitle: string } {
  const segment = title.split('|')[0]?.split(' - ')[0]?.trim() ?? title;
  const comma = segment.indexOf(',');

  if (comma > 0) {
    return {
      guestName: segment.slice(0, comma).trim(),
      guestTitle: segment.slice(comma + 1).trim(),
    };
  }

  return { guestName: 'Guest', guestTitle: segment };
}

function isoDurationToSeconds(iso?: string): number | undefined {
  if (!iso) return undefined;
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return undefined;
  return Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
}

function isPodcastEpisodeTitle(title: string): boolean {
  return PODCAST_TITLE_PATTERNS.some((pattern) => pattern.test(title));
}

/** True for Shorts and other clips under the minimum length. */
function isShortFormVideo(
  title: string,
  durationSeconds?: number,
  watchUrl?: string,
  thumbnailUrl?: string,
): boolean {
  if (/#shorts\b/i.test(title)) return true;
  if (/\bshorts\b/i.test(title) && title.length < 100) return true;
  if (watchUrl?.includes('/shorts/')) return true;
  if (thumbnailUrl?.includes('/shorts/')) return true;
  if (durationSeconds !== undefined && durationSeconds > 0 && durationSeconds < MIN_VIDEO_SECONDS) {
    return true;
  }
  return false;
}

/** Long-form podcast videos only (API always provides duration). */
function shouldIncludeVideo(
  title: string,
  durationSeconds: number | undefined,
  watchUrl?: string,
  thumbnailUrl?: string,
): boolean {
  if (durationSeconds === undefined || durationSeconds <= 0) return false;
  if (isShortFormVideo(title, durationSeconds, watchUrl, thumbnailUrl)) return false;
  if (durationSeconds < MIN_VIDEO_SECONDS) return false;
  if (isPodcastEpisodeTitle(title)) return true;
  return durationSeconds >= MIN_LONGFORM_SECONDS;
}

function formatDuration(iso?: string, seconds?: number): string {
  if (seconds && seconds > 0) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  if (!iso) return '—';

  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return '—';

  const h = Number(match[1] ?? 0);
  const m = Number(match[2] ?? 0);
  const s = Number(match[3] ?? 0);
  const totalM = h * 60 + m;

  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${totalM}:${String(s).padStart(2, '0')}`;
}

type ApiSearchItem = {
  id: { videoId: string };
  snippet: {
    title: string;
    publishedAt: string;
    thumbnails?: {
      maxres?: { url: string };
      standard?: { url: string };
      high?: { url: string };
      medium?: { url: string };
    };
  };
};

async function searchChannelVideos(
  apiKey: string,
  channelId: string,
  maxResults: number,
  videoDuration: 'medium' | 'long',
): Promise<ApiSearchItem[]> {
  const searchParams = new URLSearchParams({
    part: 'snippet',
    channelId,
    order: 'date',
    type: 'video',
    videoDuration,
    maxResults: String(Math.min(50, maxResults)),
    key: apiKey,
  });

  const searchRes = await fetch(`https://www.googleapis.com/youtube/v3/search?${searchParams}`);
  const searchData = (await searchRes.json()) as { items?: ApiSearchItem[]; error?: { message?: string } };
  if (!searchRes.ok) {
    console.warn('[YouTube API] search failed:', searchData.error?.message ?? searchRes.status);
    return [];
  }
  return searchData.items ?? [];
}

async function fetchVideoDetails(
  apiKey: string,
  videoIds: string[],
): Promise<Map<string, { duration?: string; title?: string }>> {
  const detailsMap = new Map<string, { duration?: string; title?: string }>();
  if (videoIds.length === 0) return detailsMap;

  const BATCH_SIZE = 50;
  for (let i = 0; i < videoIds.length; i += BATCH_SIZE) {
    const batch = videoIds.slice(i, i + BATCH_SIZE);
    const videoParams = new URLSearchParams({
      part: 'contentDetails,snippet',
      id: batch.join(','),
      key: apiKey,
    });

    const videoRes = await fetch(`https://www.googleapis.com/youtube/v3/videos?${videoParams}`);
    const videoData = (await videoRes.json()) as {
      items?: Array<{ id: string; contentDetails?: { duration?: string }; snippet?: { title?: string } }>;
      error?: { message?: string };
    };

    if (!videoRes.ok) {
      console.warn('[YouTube API] videos failed:', videoData.error?.message ?? videoRes.status);
      continue;
    }

    for (const v of videoData.items ?? []) {
      detailsMap.set(v.id, { duration: v.contentDetails?.duration, title: v.snippet?.title });
    }
  }

  return detailsMap;
}

function mapToEpisodes(
  searchItems: ApiSearchItem[],
  videoDetails: Map<string, { duration?: string; title?: string }>,
  options?: { playlistMode?: boolean; latestMode?: boolean },
): YouTubeEpisode[] {
  const episodes: YouTubeEpisode[] = [];

  for (const item of searchItems) {
    const id = item.id.videoId;
    const details = videoDetails.get(id);
    const title = details?.title ?? item.snippet.title;
    const durationIso = details?.duration;
    const durationSeconds = isoDurationToSeconds(durationIso);
    const watchUrl = `https://www.youtube.com/watch?v=${id}`;
    const thumbnailUrl = resolveYouTubeThumbnailUrl(id, item.snippet.thumbnails);

    const passesShortFilter =
      durationSeconds !== undefined &&
      durationSeconds > 0 &&
      !isShortFormVideo(title, durationSeconds, watchUrl, thumbnailUrl) &&
      durationSeconds >= MIN_VIDEO_SECONDS;

    const include = options?.playlistMode || options?.latestMode
      ? passesShortFilter
      : shouldIncludeVideo(title, durationSeconds, watchUrl, thumbnailUrl);

    if (!include) continue;

    const { guestName, guestTitle } = parseGuestFromTitle(title);

    episodes.push({
      id,
      title,
      guestName,
      guestTitle,
      thumbnailUrl,
      duration: formatDuration(durationIso, durationSeconds),
      publishedAt: item.snippet.publishedAt,
      watchUrl,
    });
  }

  return episodes;
}

/** Fetch long-form channel videos via YouTube Data API (no RSS). */
async function fetchViaYouTubeApi(
  apiKey: string,
  channelId: string,
  maxResults: number,
  options?: {
    titleFilter?: (title: string) => boolean;
    searchPool?: number;
  },
): Promise<YouTubeEpisode[]> {
  const pool = options?.searchPool ?? maxResults;
  const perBucket = Math.min(50, Math.max(pool * 8, maxResults * 8));

  const [mediumItems, longItems] = await Promise.all([
    searchChannelVideos(apiKey, channelId, perBucket, 'medium'),
    searchChannelVideos(apiKey, channelId, perBucket, 'long'),
  ]);

  const seen = new Set<string>();
  const items: ApiSearchItem[] = [];
  for (const item of [...mediumItems, ...longItems]) {
    const id = item.id.videoId;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    items.push(item);
  }

  if (items.length === 0) return [];

  items.sort((a, b) => Date.parse(b.snippet.publishedAt) - Date.parse(a.snippet.publishedAt));

  const detailsMap = await fetchVideoDetails(
    apiKey,
    items.map((i) => i.id.videoId),
  );

  let episodes = mapToEpisodes(items, detailsMap);
  if (options?.titleFilter) {
    episodes = episodes.filter((episode) => options.titleFilter!(episode.title));
  }

  return episodes.slice(0, maxResults);
}

type PlaylistItemSnippet = {
  title: string;
  publishedAt: string;
  thumbnails?: {
    maxres?: { url: string };
    standard?: { url: string };
    high?: { url: string };
    medium?: { url: string };
  };
  resourceId?: { videoId?: string };
};

async function fetchPlaylistSearchItems(
  apiKey: string,
  playlistId: string,
  maxResults: number,
): Promise<ApiSearchItem[]> {
  const items: ApiSearchItem[] = [];
  let pageToken: string | undefined;

  while (items.length < maxResults) {
    const params = new URLSearchParams({
      part: 'snippet',
      playlistId,
      maxResults: String(Math.min(50, maxResults - items.length)),
      key: apiKey,
    });
    if (pageToken) params.set('pageToken', pageToken);

    const res = await fetch(`https://www.googleapis.com/youtube/v3/playlistItems?${params}`);
    const data = (await res.json()) as {
      items?: Array<{ snippet: PlaylistItemSnippet }>;
      nextPageToken?: string;
      error?: { message?: string };
    };

    if (!res.ok) {
      console.warn('[YouTube API] playlistItems failed:', data.error?.message ?? res.status);
      break;
    }

    for (const item of data.items ?? []) {
      const videoId = item.snippet.resourceId?.videoId;
      if (!videoId) continue;
      items.push({
        id: { videoId },
        snippet: {
          title: item.snippet.title,
          publishedAt: item.snippet.publishedAt,
          thumbnails: item.snippet.thumbnails,
        },
      });
    }

    pageToken = data.nextPageToken;
    if (!pageToken) break;
  }

  return items;
}

async function fetchYouTubePlaylistEpisodes(
  apiKey: string,
  playlistId: string,
  maxResults: number,
): Promise<YouTubeEpisode[]> {
  const items = await fetchPlaylistSearchItems(apiKey, playlistId, maxResults);
  if (items.length === 0) return [];

  const detailsMap = await fetchVideoDetails(
    apiKey,
    items.map((i) => i.id.videoId),
  );

  return mapToEpisodes(items, detailsMap, { playlistMode: true }).slice(0, maxResults);
}

function channelUploadsPlaylistId(channelId: string): string {
  return channelId.startsWith('UC') ? `UU${channelId.slice(2)}` : channelId;
}

/** Latest channel uploads (any video, Shorts excluded) — not filtered by podcast show. */
async function fetchLatestChannelUploads(
  apiKey: string,
  channelId: string,
  maxResults: number,
): Promise<YouTubeEpisode[]> {
  const playlistId = channelUploadsPlaylistId(channelId);
  const items = await fetchPlaylistSearchItems(apiKey, playlistId, Math.max(maxResults * 4, 24));
  if (items.length === 0) return [];

  const detailsMap = await fetchVideoDetails(
    apiKey,
    items.map((i) => i.id.videoId),
  );

  return mapToEpisodes(items, detailsMap, { latestMode: true }).slice(0, maxResults);
}

/** Latest channel videos from YouTube Data API v3 (Shorts excluded, all uploads). */
export async function fetchLatestYouTubeEpisodes(maxResults = 6): Promise<YouTubeEpisode[]> {
  const apiKey = config.youtubeApiKey;
  const channelId = config.youtubeChannelId || DEFAULT_CHANNEL_ID;

  if (!apiKey) {
    console.warn('[YouTube] Set REACT_APP_YOUTUBE_API_KEY in .env to load podcast episodes.');
    return [];
  }

  try {
    return await fetchLatestChannelUploads(apiKey, channelId, maxResults);
  } catch (err) {
    console.warn('[YouTube] Latest uploads fetch failed:', err);
    return [];
  }
}

/** Episodes for a specific podcast show from its YouTube playlist. */
export async function fetchYouTubeEpisodesForShow(
  showId: string,
  maxResults = 12,
): Promise<YouTubeEpisode[]> {
  const apiKey = config.youtubeApiKey;

  if (!apiKey) {
    console.warn('[YouTube] Set REACT_APP_YOUTUBE_API_KEY in .env to load podcast episodes.');
    return [];
  }

  const show = getPodcastShowById(showId);
  if (!show?.youtubePlaylistId) return [];

  try {
    return await fetchYouTubePlaylistEpisodes(apiKey, show.youtubePlaylistId, maxResults);
  } catch (err) {
    console.warn('[YouTube] Playlist fetch failed:', err);

    const channelId = config.youtubeChannelId || DEFAULT_CHANNEL_ID;
    if (!PODCAST_SHOW_TITLE_PATTERNS[showId]) return [];

    try {
      return await fetchViaYouTubeApi(apiKey, channelId, maxResults, {
        titleFilter: (title) => episodeMatchesPodcastShow(title, showId),
        searchPool: 60,
      });
    } catch (fallbackErr) {
      console.warn('[YouTube] API fetch failed:', fallbackErr);
      return [];
    }
  }
}
